import React from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockChip from '../components/GuideMockChip';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideAssessmentSearch: React.FC = () => {
  return (
    <GuidePageLayout
      title="資産を検索する"
      description="条件を指定して資産を絞り込み検索します。"
      breadcrumbs={[{ label: '資産管理', path: '/guide/assessment/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', 'ログイン済み（権限不問）'],
            ['ログイン状態', 'ログイン済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: '資産管理画面を開く' },
            {
              label: '検索条件を入力する（全て任意、未入力で全件検索）',
              content: (
                <GuideMockContainer label="検索条件チップ">
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <GuideMockChip label="資産区分: PC" color="secondary" />
                    <GuideMockChip label="資産名: ノート" color="info" />
                    <GuideMockChip label="利用者: ユーザーA" color="info" />
                    <GuideMockChip label="除却済みを含む" color="default" />
                  </Stack>
                </GuideMockContainer>
              ),
            },
            { label: '「検索」ボタンを押下する' },
            { label: '検索結果が一覧表示される' },
          ]}
        />
      </GuideSection>

      <GuideSection title="検索条件">
        <GuideTable
          headers={['項目名', '形式', '検索方法', '備考']}
          rows={[
            ['資産区分', 'プルダウン', '完全一致', 'PC / モニター / 周辺機器'],
            ['資産名', 'テキスト', '部分一致', ''],
            ['管理番号1', 'テキスト', '完全一致', ''],
            ['管理番号2', 'テキスト', '完全一致', ''],
            ['管理番号3', 'テキスト', '完全一致', ''],
            ['利用者', 'プルダウン', '完全一致', 'ユーザー一覧から選択'],
            ['管理者', 'プルダウン', '完全一致', 'ユーザー一覧から選択'],
            ['情報', 'テキスト', '部分一致', '情報1〜3を横断検索'],
            ['除却済みを含む', 'チェックボックス', '—', 'ONで除却済みも表示'],
            ['除却日From', '日付', '範囲（以降）', ''],
            ['除却日To', '日付', '範囲（以前）', ''],
            ['登録日From', '日付', '範囲（以降）', ''],
            ['登録日To', '日付', '範囲（以前）', ''],
          ]}
        />
      </GuideSection>

      <GuideSection title="検索結果一覧の表示">
        <GuideTable
          headers={['表示項目', '位置', '説明']}
          rows={[
            ['資産名', 'メイン（左）', '資産の表示名'],
            ['資産区分Chip', 'メイン（右）', '資産区分の名称を表示'],
            ['除却済Chip', 'メイン（右）', '除却済みの場合のみ表示'],
            ['利用者 / 管理者 / 登録日', 'サブテキスト', '利用者名、管理者名、登録日を表示'],
          ]}
        />
        <GuideMockContainer label="リスト項目の表示例">
          <Stack spacing={1.5}>
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" fontWeight={500}>
                  ノートPC A
                </Typography>
                <GuideMockChip label="PC" color="info" size="small" />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                利用者: 田中太郎　管理者: 山田花子　登録日: 2025/04/01
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" fontWeight={500}>
                  モニター B
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <GuideMockChip label="モニター" color="info" size="small" />
                  <GuideMockChip label="除却済" color="error" size="small" />
                </Stack>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                利用者: 佐藤次郎　管理者: 山田花子　登録日: 2024/10/15
              </Typography>
            </Stack>
          </Stack>
        </GuideMockContainer>
        <GuideTable
          headers={['Chip', '意味', '表示条件', '色']}
          rows={[
            ['資産区分名', '資産の区分（PC / モニター / 周辺機器等）', '常に表示', '青（info）'],
            ['除却済', '資産が除却済み', '除却フラグがONの場合', '赤（error）'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>
            デフォルトでは未除却の資産のみ表示されます。除却済みの資産を確認したい場合は「除却済みを含む」にチェックを入れてください。
          </GuideInfoBox>
          <GuideInfoBox>表示件数には上限があります。条件を絞って検索してください。</GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideAssessmentSearch;
